import { useState } from 'react';
import Button from 'components/Button';
import {
    approveSponsorAnswers,
    declineSponsorAnswers,
    editSponsorAnswers,
} from 'api/faq-sponsor';
import { newsroomError } from 'api/newsroom';
import { MAX_ANSWER_WORDS, countWords } from 'utils/faq-sponsor';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const REVIEWABLE = ['in_review', 'live'];

// Admin: the sponsor's answers for this story. Approve puts the block live;
// Decline emails the sponsor the reason (the payment is refunded in Stripe).
const FaqReview = ({ alertId, story, onStoryChange, onNotice }) => {
    const status = story.faqSponsorStatus;
    const [answers, setAnswers] = useState(story.faqAnswers ?? []);
    const [website, setWebsite] = useState(story.faqSponsorWebsite ?? '');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(null);

    const run = async (key, action, done, fallback) => {
        setBusy(key);
        onNotice(null);
        try {
            const result = await action();
            const saved = key === 'decline' ? result.story : result;
            onStoryChange(saved);
            onNotice({
                type: 'ok',
                text: (key === 'decline' && result.message) || done,
            });
        } catch (err) {
            onNotice({ type: 'error', text: newsroomError(err, fallback) });
        } finally {
            setBusy(null);
        }
    };

    const unsaved =
        JSON.stringify(answers) !== JSON.stringify(story.faqAnswers ?? []) ||
        website !== (story.faqSponsorWebsite ?? '');
    const canDecline = ['awaiting_answers', 'in_review', 'live'].includes(
        status
    );
    if (!REVIEWABLE.includes(status) && !canDecline) return null;

    return (
        <div className={styles.faqReview}>
            {REVIEWABLE.includes(status) &&
                (story.faqQuestions ?? []).map((question, i) => (
                    <div key={i}>
                        <label
                            className={styles.label}
                            htmlFor={`faq-answer-${i}`}
                        >
                            {question}
                        </label>
                        <textarea
                            id={`faq-answer-${i}`}
                            className={styles.textarea}
                            rows={5}
                            value={answers[i] ?? ''}
                            onChange={(e) =>
                                setAnswers((current) =>
                                    Object.assign([...current], {
                                        [i]: e.target.value,
                                    })
                                )
                            }
                        />
                        <span
                            className={
                                countWords(answers[i]) > MAX_ANSWER_WORDS
                                    ? styles.overLimit
                                    : styles.hint
                            }
                        >
                            {`${countWords(
                                answers[i]
                            )} / ${MAX_ANSWER_WORDS} words`}
                        </span>
                    </div>
                ))}
            {REVIEWABLE.includes(status) && (
                <>
                    <label className={styles.label} htmlFor="faq-website">
                        Sponsor&rsquo;s website (sponsored link)
                    </label>
                    <input
                        id="faq-website"
                        className={styles.input}
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                    />
                    <div className={styles.editorActions}>
                        <Button
                            title={'Save edits'}
                            theme={'secondary'}
                            loading={busy === 'edit'}
                            onClick={() =>
                                run(
                                    'edit',
                                    () =>
                                        editSponsorAnswers(alertId, {
                                            answers,
                                            website,
                                        }),
                                    status === 'live'
                                        ? 'Saved. The live answers are updated.'
                                        : 'Saved.',
                                    'Could not save the answers.'
                                )
                            }
                        />
                        {status === 'in_review' && (
                            <Button
                                title={'Approve and publish'}
                                theme={'success'}
                                loading={busy === 'approve'}
                                disabled={unsaved}
                                onClick={() =>
                                    run(
                                        'approve',
                                        () => approveSponsorAnswers(alertId),
                                        'Approved. The expert answers are live under the story.',
                                        'Could not approve the answers.'
                                    )
                                }
                            />
                        )}
                    </div>
                    {unsaved && status === 'in_review' && (
                        <p className={styles.hint}>
                            Save your edits before approving.
                        </p>
                    )}
                </>
            )}
            {canDecline && (
                <>
                    <label className={styles.label} htmlFor="faq-decline">
                        {status === 'live'
                            ? 'Reason for taking the answers down (emailed to the sponsor)'
                            : 'Reason for declining (emailed to the sponsor)'}
                    </label>
                    <textarea
                        id="faq-decline"
                        className={styles.textarea}
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <div className={styles.editorActions}>
                        <Button
                            title={status === 'live' ? 'Take down' : 'Decline'}
                            theme={'danger'}
                            loading={busy === 'decline'}
                            disabled={!reason.trim()}
                            onClick={() =>
                                run(
                                    'decline',
                                    () =>
                                        declineSponsorAnswers(alertId, reason),
                                    'Declined and the sponsor emailed. Refund the payment in Stripe.',
                                    'Could not decline.'
                                )
                            }
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default FaqReview;
